import React from "react"
import { Box, Typography } from "@mui/material"
import WavesIcon from "@mui/icons-material/Waves"
import AirIcon from "@mui/icons-material/Air"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined"
import {
  DIVIDER,
  INK,
  IconCircle,
  LimitBar,
  MUTED,
  ORANGE,
  RED,
  SECTION_LABEL_SX,
  Timeline,
  fmt,
} from "@/features/route/alertCardParts.jsx"
const WEATHER_META = {
  waves: { label: "Waves", Icon: WavesIcon },
  wind: { label: "Wind", Icon: AirIcon },
  current: { label: "Current", Icon: SwapHorizIcon },
}

const ZONE_LABELS = {
  high_risk: "High risk area",
  war_risk: "War risk area",
  operational_risk: "Operational risk area",
  eca: "ECA",
}

const paletteOf = (section) =>
  section.type === "zone" && section.zoneKind === "eca" ? ORANGE : RED


function WeatherSection({ section, palette, compact }) {
  const { label, Icon } = WEATHER_META[section.parameter] ?? WEATHER_META.waves
  const over = section.max != null && section.limit != null ? section.max - section.limit : null
  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <IconCircle Icon={Icon} palette={palette} size={30} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Typography sx={{ ...SECTION_LABEL_SX, color: MUTED }}>
            {label}
          </Typography>
          {over != null && over > 0 && (
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: palette.color }}>
              +{fmt(over, section.unit)} {section.unit}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
            {fmt(section.max, section.unit)}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED }}>{section.unit} max</Typography>
        </Box>
        <LimitBar max={section.max} limit={section.limit} unit={section.unit} color={palette.color} compact={compact} />
      </Box>
    </Box>
  )
}

function ZoneSection({ section, palette, compact }) {
  return (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
      <IconCircle Icon={GppMaybeOutlinedIcon} palette={palette} size={compact ? 30 : 36} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...SECTION_LABEL_SX, color: palette.color }}>
          {ZONE_LABELS[section.zoneKind] ?? section.zoneKind}
        </Typography>
        <Typography sx={{ fontSize: compact ? 15 : 17, fontWeight: 700, color: INK, lineHeight: 1.25 }}>
          {section.name}
        </Typography>
        {section.attributes?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {section.attributes.map((a) => (
              <Typography
                component="span"
                key={a.label}
                sx={{
                  height: 20,
                  px: 1,
                  borderRadius: 10,
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  color: a.emphasis ? "white" : palette.color,
                  background: a.emphasis ? palette.color : palette.tint,
                }}
              >
                {a.label}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}


const hasOwnSpan = (s) => !!(s.entry || s.exit)

// Weather/zone sections at one map spot; shared timeline unless the sections carry their own spans.
export default function TransitCardBody({ sections = [], entry, exit, durationH }) {
  const perSection = sections.some(hasOwnSpan)
  const sharedColor = sections.some((s) => paletteOf(s) === RED) ? RED.color : ORANGE.color
  return (
    <Box sx={{ pt: 1.5, pb: 1.25, pl: 1.75, pr: perSection ? 2.25 : 1.75 }}>
      {sections.map((s, i) => {
        const palette = paletteOf(s)
        return (
          <Box
            key={`${s.type}-${s.parameter ?? s.zoneKind}-${s.name ?? ""}-${i}`}
            sx={i > 0 ? { mt: perSection ? 0.5 : 1.5, pt: perSection ? 0.5 : 1.5, borderTop: `1px solid ${DIVIDER}` } : null}
          >
            {s.type === "weather" ? (
              <WeatherSection section={s} palette={palette} compact={perSection} />
            ) : (
              <ZoneSection section={s} palette={palette} compact={hasOwnSpan(s)} />
            )}
            {perSection && (
              <Timeline entry={s.entry} exit={s.exit} durationH={s.durationH} color={palette.color} compact />
            )}
          </Box>
        )
      })}
      {!perSection && <Timeline entry={entry} exit={exit} durationH={durationH} color={sharedColor} />}
      <Typography sx={{ mt: 1.25, fontSize: 10, color: MUTED }}>Times approx. · UTC</Typography>
    </Box>
  )
}
